import { Component, ReactNode } from 'react';
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';

/**
 * 화면 단위 오류 격리.
 *
 * React 는 렌더 중 예외가 나면 트리 전체를 언마운트한다. 경계가 없으면 화면 하나의 버그가
 * **앱 전체를 빈 페이지로** 만들고, 라우터도 함께 죽어 뒤로가기조차 듣지 않는다
 * (2026-09-01 조회 화면에서 실제 발생 — 객체를 그대로 렌더한 것이 원인이었다).
 *
 * 그래서 라우트 내용만 감싸고 좌측 네비·헤더는 살려 둔다. 사용자가 다른 메뉴로 빠져나갈 수 있어야 한다.
 */
interface Props {
  children: ReactNode;
  /** 라우트 경로 — 바뀌면 오류 상태를 자동으로 푼다(다른 메뉴로 이동하면 복구) */
  resetKey?: string;
}
interface State {
  error: Error | null;
  shownFor: string | undefined;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, shownFor: undefined };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    // 경로가 바뀌었으면 오류를 지운다 — 사용자가 메뉴를 눌러 빠져나갈 수 있어야 한다
    if (state.error && state.shownFor !== undefined && state.shownFor !== props.resetKey) {
      return { error: null, shownFor: props.resetKey };
    }
    if (!state.error) return { shownFor: props.resetKey };
    return null;
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }): void {
    // 운영 중 원인 추적용 — 콘솔에 남긴다
    console.error('[화면 오류]', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <Paper sx={{ p: 4, maxWidth: 720 }}>
        <Stack spacing={2}>
          <Typography variant="h2">화면을 표시할 수 없습니다</Typography>
          <Alert severity="error">
            이 화면을 그리는 중 오류가 발생했습니다. 다른 메뉴는 정상 동작하므로 계속 사용하실 수 있습니다.
          </Alert>
          <Box
            sx={{
              fontFamily: 'monospace', fontSize: 12, color: 'text.secondary',
              bgcolor: '#f7f8fa', p: 2, borderRadius: 1, wordBreak: 'break-all',
            }}
          >
            {this.state.error.message}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => this.setState({ error: null })}>다시 시도</Button>
            <Button onClick={() => window.location.reload()}>새로고침</Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }
}
